const LOCKOUT_STORAGE_PREFIX = "login_lockout_";

export const formatCountdown = (totalSeconds) => {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const mm = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const ss = (seconds % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
};

export const getLockoutStorageKey = (email) =>
  `${LOCKOUT_STORAGE_PREFIX}${String(email || "")
    .trim()
    .toLowerCase()}`;

export const getRemainingSeconds = (lockedUntil, retryAfterSeconds) => {
  if (lockedUntil) {
    const diff = Math.ceil((new Date(lockedUntil).getTime() - Date.now()) / 1000);
    return Math.max(0, diff);
  }
  if (retryAfterSeconds != null) {
    return Math.max(0, Number(retryAfterSeconds) || 0);
  }
  return 0;
};

export const isAccountLockout = (payload) => {
  if (!payload) return false;
  if (payload.status === 429) return true;
  if (payload.lockedUntil) return true;
  if (
    payload.remainingAttempts === 0 &&
    payload.retryAfterSeconds != null
  ) {
    return true;
  }
  return false;
};

export const normalizeApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") {
    return { error: data, status: error?.response?.status };
  }
  return {
    ...(data || {}),
    status: error?.response?.status,
    error: data?.error || data?.message || fallback,
  };
};

export const parseLockoutFromPayload = (payload) => {
  if (!payload) return null;

  const hasLockoutData =
    payload.status === 429 ||
    payload.lockedUntil ||
    (payload.remainingAttempts === 0 && payload.retryAfterSeconds != null);

  if (!hasLockoutData && !isAccountLockout(payload)) return null;

  return {
    lockedUntil: payload.lockedUntil || null,
    retryAfterSeconds: payload.retryAfterSeconds ?? null,
  };
};

export const saveLockoutToSession = (email, lockout) => {
  if (!email || !lockout) return;
  try {
    sessionStorage.setItem(
      getLockoutStorageKey(email),
      JSON.stringify({
        lockedUntil: lockout.lockedUntil,
        retryAfterSeconds: lockout.retryAfterSeconds,
      })
    );
  } catch {
    // sessionStorage may be unavailable
  }
};

export const loadLockoutFromSession = (email) => {
  if (!email) return null;
  try {
    const raw = sessionStorage.getItem(getLockoutStorageKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const remainingSeconds = getRemainingSeconds(
      parsed.lockedUntil,
      parsed.retryAfterSeconds
    );
    if (remainingSeconds <= 0) {
      sessionStorage.removeItem(getLockoutStorageKey(email));
      return null;
    }
    return {
      lockedUntil: parsed.lockedUntil,
      retryAfterSeconds: parsed.retryAfterSeconds,
      remainingSeconds,
    };
  } catch {
    return null;
  }
};

export const clearLockoutFromSession = (email) => {
  if (!email) return;
  try {
    sessionStorage.removeItem(getLockoutStorageKey(email));
  } catch {
    // ignore
  }
};
